
import { XMarkIcon as XIcon } from '@heroicons/vue/20/solid'
import { nextTick } from 'vue'
import Button from '@/components/Button.vue'
import api from '@/store/api'
import { useStore } from 'vuex'

export default {
  name: 'AddDeliveryAddressForm',
  components: {
    Button,
    XIcon
  },
  setup() {
    const store = useStore()
    return { store }
  },
  props: {
    passedDeliveryAddress: {
      type: Object,
      required: false
    },
    forceOpen: {
      type: Boolean,
      required: false,
      default: false
    },
    conversationUserPairId: {
      type: [String, Number],
      required: true
    },
    canAdd: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
    isDefault: false,
    isAddingAddress: false,
    errorMessage: '',
    replacingAddressId: undefined,
    showingAdd: false,
    googlePlacesInitialized: false,
    autocompleteInstance: null
  }),
  computed: {
    statePlaceholder() {
      if (this.merchantCountry === 'USA') return 'State'
      if (this.merchantCountry === 'GBR') return 'County'
      return 'Province'
    },
    selectButtonTitle() {
      if (this.passedDeliveryAddress) {
        return 'Update & Save'
      } else {
        return 'Add & Save'
      }
    },
    title() {
      if (this.passedDeliveryAddress) {
        return 'Update an address'
      } else {
        return 'Add an address'
      }
    },
    validAddress() {
      try {
        const minZipLength = { USA: 5, AUS: 4, CAN: 7, GBR: 7 }[this.merchantCountry] || 5
        const result = this.address1 && this.city && this.state && this.zip && this.zip.length >= minZipLength

        return result
      } catch (error) {
        console.error('Validation error:', error)
        return false
      }
    },
    hasErrorMessage() {
      return this.errorMessage.length > 0
    },
    merchantCountry() {
      return this.store.getters.country || 'USA'
    }
  },
  watch: {
    state(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.errorMessage = ''
      }
    },
    passedDeliveryAddress: {
      handler(newValue) {
        if (newValue) {
          this.address1 = newValue.attributes.address1 || ''
          this.address2 = newValue.attributes.address2 || ''
          this.city = newValue.attributes.city || ''
          this.state = newValue.attributes.state || ''
          this.zip = newValue.attributes.zip || ''
          this.notes = newValue.attributes.notes || ''
          this.isDefault = !!newValue.attributes.is_default
          this.replacingAddressId = newValue.id
          this.showingAdd = true
        }
      },
      immediate: true
    },
    forceOpen: {
      handler(newValue) {
        if (newValue) {
          this.showingAdd = true
          nextTick(() => this.$refs.addr1?.focus())
        }
      },
      immediate: true
    },
    merchantCountry: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue && window.google && window.google.maps) {
          this.googlePlacesInitialized = false
          if (newValue && newValue !== 'USA') {
            this.initializeGooglePlaces()
          }
        }
      }
    }
  },
  mounted() {
    this.showingAdd = this.forceOpen
  },
  methods: {
    initializeGooglePlaces() {
      if (this.googlePlacesInitialized) return

      const countryCode = { USA: 'us', AUS: 'au', CAN: 'ca', GBR: 'gb' }[this.merchantCountry]

      if (this.autocompleteInstance) {
        google.maps.event.clearInstanceListeners(this.autocompleteInstance)
        this.autocompleteInstance = null
      }

      google.maps.importLibrary('places').then(({ Autocomplete }) => {
        const autocomplete = new Autocomplete(this.$refs.addr1, {
          componentRestrictions: { country: countryCode },
          fields: ['address_components', 'formatted_address', 'geometry', 'place_id']
        })
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace()

          this.address1 = ''
          this.address2 = ''
          this.city = ''
          this.state = ''
          this.zip = ''

          for (const comp of place.address_components) {
            for (const type of comp.types) {
              switch (type) {
                case 'street_number':
                  this.address1 = comp.long_name
                  break
                case 'route':
                  if (this.address1) {
                    this.address1 += ' ' + comp.short_name
                  } else {
                    this.address1 = comp.short_name
                  }
                  break
                case 'subpremise':
                  this.address2 = comp.long_name
                  break
                case 'locality':
                  if (!this.city) {
                    this.city = comp.long_name
                  }
                  break
                case 'sublocality_level_1':
                  if (!this.city) {
                    this.city = comp.long_name
                  }
                  break
                case 'postal_town':
                  if (!this.city) {
                    this.city = comp.long_name
                  }
                  break
                case 'administrative_area_level_1':
                  if (this.merchantCountry === 'GBR') {
                    const mappedState = this.mapUKCountyToCode(comp.long_name)
                    if (mappedState !== null) {
                      this.state = mappedState
                    }
                  } else {
                    this.state = comp.short_name
                  }
                  break
                case 'administrative_area_level_2':
                  if (this.merchantCountry === 'GBR' && !this.state) {
                    const mappedState = this.mapUKCountyToCode(comp.long_name)
                    if (mappedState !== null) {
                      this.state = mappedState
                    }
                  }
                  break
                case 'country':
                  break
                case 'postal_code':
                  this.zip = comp.long_name
                  break
              }
            }
          }

          if (!this.city && place.formatted_address) {
            const parts = place.formatted_address.split(',')
            if (parts.length > 1) {
              this.city = parts[1].trim()
            }
          }

          nextTick(() => this.$refs.addr2?.focus())
        })
        this.autocompleteInstance = autocomplete
        this.googlePlacesInitialized = true
      })
    },
    showAdd() {
      this.showingAdd = true
      nextTick(() => {
        this.$refs.addr1?.focus()
        if (window.google && window.google.maps) {
          this.googlePlacesInitialized = false
          this.initializeGooglePlaces()
        }
      })
    },
    addAddress() {
      this.isAddingAddress = true
      this.errorMessage = ''

      const addressData = {
        address1: this.address1,
        address2: this.address2,
        city: this.city,
        state: this.state,
        zip: this.zip,
        notes: this.notes,
        country: this.merchantCountry,
        is_default: this.isDefault === true
      }

      if (this.replacingAddressId) {
        api
          .call(
            'PUT',
            `conversation_user_pairs/${this.conversationUserPairId}/customer/delivery_address/${this.replacingAddressId}`,
            {
              delivery_address: addressData
            }
          )
          .then(
            response => {
              this.isAddingAddress = false
              this.clearFields()
              this.showingAdd = false
              this.$emit('delivery-address-saved')
            },
            error => {
              this.isAddingAddress = false
              this.errorMessage = 'Failed to save address. Please try again.'
            }
          )
      } else {
        api
          .call('POST', `conversation_user_pairs/${this.conversationUserPairId}/customer/delivery_address`, {
            delivery_address: addressData
          })
          .then(
            response => {
              this.isAddingAddress = false
              this.clearFields()
              this.showingAdd = false
              this.$emit('delivery-address-saved')
            },
            error => {
              this.isAddingAddress = false
              this.errorMessage = 'Failed to save address. Please try again.'
            }
          )
      }
    },
    clearFields() {
      this.address1 = ''
      this.address2 = ''
      this.city = ''
      this.state = ''
      this.zip = ''
      this.notes = ''
      this.isDefault = false
      this.replacingAddressId = undefined
    },

    mapUKCountyToCode(countyName) {
      const countryNames = ['England', 'Scotland', 'Wales', 'Northern Ireland', 'United Kingdom', 'UK']
      if (countryNames.includes(countyName)) {
        return null
      }

      const countyMapping = {
        Bedfordshire: 'BED',
        Berkshire: 'BERK',
        Buckinghamshire: 'BUCK',
        Cambridgeshire: 'CAM',
        Cheshire: 'CHES',
        Cornwall: 'CORN',
        Cumbria: 'CUMB',
        Derbyshire: 'DERB',
        Devon: 'DEV',
        Dorset: 'DOR',
        'County Durham': 'DUR',
        'East Riding of Yorkshire': 'ERYORK',
        'East Sussex': 'ESX',
        Essex: 'ESS',
        Gloucestershire: 'GLS',
        'Greater Manchester': 'GMAN',
        Hampshire: 'HANTS',
        Hertfordshire: 'HERTS',
        Herefordshire: 'HANTS',
        'Isle of Wight': 'IOW',
        Kent: 'KENT',
        Lancashire: 'LANCS',
        Leicestershire: 'LEICS',
        Lincolnshire: 'LINCS',
        'Greater London': 'LOND',
        Merseyside: 'MER',
        Norfolk: 'NORF',
        Northamptonshire: 'NHANTS',
        Northumberland: 'NTHUMB',
        Nottinghamshire: 'NOTTS',
        Oxfordshire: 'OXON',
        Rutland: 'RUT',
        Shropshire: 'SHROP',
        Somerset: 'SOM',
        Staffordshire: 'STS',
        Suffolk: 'SUFF',
        Surrey: 'SURREY',
        'Tyne and Wear': 'TYNE',
        Warwickshire: 'WARKS',
        'West Midlands': 'W MIDS',
        'West Sussex': 'W SUSX',
        Wiltshire: 'WILTS',
        Worcestershire: 'WORCS',
        'North Yorkshire': 'YORK',
        'South Yorkshire': 'SYORK',
        'West Yorkshire': 'WYORK',
        Wales: 'WALES',
        Scotland: 'SCOT',
        'Northern Ireland': 'NIRE'
      }

      if (countyMapping[countyName]) {
        return countyMapping[countyName]
      }

      const lowerCountyName = countyName.toLowerCase()
      for (const [fullName, code] of Object.entries(countyMapping)) {
        if (lowerCountyName.includes(fullName.toLowerCase()) || fullName.toLowerCase().includes(lowerCountyName)) {
          return code
        }
      }

      return null
    },

    updateCloseClicked() {
      this.clearFields()
      this.showingAdd = false
      this.$emit('delivery-address-edit-canceled')
    }
  }
}
